import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { User, LogOut, Settings, Sliders, Volume2, Sparkles, Target, Shield, School, Edit2, Save, X, Link as LinkIcon, Copy, CheckCircle2, Eye, Heart, GraduationCap, Camera, Trash2, Calendar, AlertCircle, Users, RefreshCw } from 'lucide-react';
import { Toggle } from '../ui/Toggle';
import { Button } from '../ui/Button';
import { RecoveryCodeSection } from './RecoveryCodeSection';
import { Input } from '../ui/Input';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { AppState } from '../../app/types';
import { GroupId, StartingPeriod } from '../../config/systemConfig';
import { type AccountsState, isLearnerAccount, isTeacherAccount, JOIN_CODE_LENGTH, JOIN_CODE_EXAMPLE } from '../../accounts';
import { getAccountRoleLabel } from '../../accounts/logic/uiHelpers';
import { deleteAssessmentDataForLearner } from '../../assessment/utils/assessmentStorage';
import { isRosterEnabled, setRosterEnabled, forgetProfile } from '../../data/supabase/deviceRoster';
import { PageContainer } from '../shared/PageContainer';
import { PageHero } from '../shared/PageHero';
import { WorldZone } from '../shared/WorldZone';
import { itemVariants } from '../shared/animations';
import { useDjaySelection } from '../../mascot';
import { dataLayer, type LearnerPreferences } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { getProgressToNextLevel } from '../../config/constants';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ProgressBar } from '../ui/Progress';
import { soundManager } from '../../utils/soundManager';
import { playSoundIntent, type SoundIntent } from '../../utils/sound/soundIntent';
import { getAutoPhaseLabel } from '../../mastery';
import { getSessionState, subscribeToSession, shouldUseBackend, fetchLinkedPeople, refreshProfile, type SessionState } from '../../data/supabase/session';
import { pushSoloProgress } from '../../data/supabase/soloProgressSync';
import { pseudonymFor } from '../../portals/shared/learnerPseudonym';
import { regenerateOwnLinkCode, fetchOwnLinkCode } from '../../data/supabase/linkCode';
import { useStore } from '../../app/store';
const DEFAULT_PREFS: LearnerPreferences = {
  sound: true,
  reducedMotion: false,
  dailyGoal: 10
};
// App-Feel 2027 W2 — sound-mix sliders. Each control maps a persisted 0..1
// volume field to a category, plus a preview intent played when the value is
// committed so the child immediately hears the effect of the slider.
const SOUND_MIX_SLIDERS: {
  key: 'masterVolume' | 'voiceVolume' | 'celebrationVolume' | 'uiVolume';
  label: string;
  hint: string;
  accent: string;
  preview: SoundIntent;
}[] = [{
  key: 'masterVolume',
  label: 'Hoofdvolume',
  hint: 'Alle geluiden samen',
  accent: 'orange',
  preview: 'reward.streak'
}, {
  key: 'voiceVolume',
  label: "Djay's stem",
  hint: 'Het blup-geluidje van Djay',
  accent: 'sky',
  preview: 'mascot.speak'
}, {
  key: 'celebrationVolume',
  label: 'Vieringen',
  hint: 'Goede antwoorden en beloningen',
  accent: 'amber',
  preview: 'reward.levelUp'
}, {
  key: 'uiVolume',
  label: 'Klikgeluiden',
  hint: 'Tikken, klikken en navigeren',
  accent: 'emerald',
  preview: 'nav.click'
}];
const ACCENT_RANGE: Record<string, string> = {
  orange: 'accent-orange-500',
  sky: 'accent-sky-500',
  amber: 'accent-amber-500',
  emerald: 'accent-emerald-500'
};
const ACCENT_TEXT: Record<string, string> = {
  orange: 'text-orange-600',
  sky: 'text-sky-600',
  amber: 'text-amber-600',
  emerald: 'text-emerald-600'
};
interface SettingsPageProps {
  state: AppState;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod;
  manualPeriod?: StartingPeriod | null;
  onSetStartingPeriod: (period: StartingPeriod | null) => void;
  onUpdateName?: (newName: string) => void;
  accounts?: AccountsState;
  onLogout?: () => void;
  viewingLearnerId?: string | null;
}
export function SettingsPage({
  state,
  selectedGroup,
  startingPeriod,
  manualPeriod,
  onSetStartingPeriod,
  onUpdateName,
  accounts,
  onLogout,
  viewingLearnerId
}: SettingsPageProps) {
  const {
    resetLearnerProgress,
    joinClassroomByCode,
    deleteAccount,
    setLearnerLinkCode
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  // Persisted preferences — loaded async from dataLayer
  const [prefs, setPrefs] = useState<LearnerPreferences>(DEFAULT_PREFS);
  // Load preferences from dataLayer on mount
  useEffect(() => {
    dataLayer.getLearnerPreferences().then(setPrefs).catch(() => {});
  }, []);
  // Save preferences to dataLayer on change
  useEffect(() => {
    dataLayer.saveLearnerPreferences(prefs);
  }, [prefs]);
  // ── Opslagstatus ───────────────────────────────────────────────────
  // Tijdens de migratie naar Supabase is niet zichtbaar of een account écht
  // op de server staat of alleen lokaal. Dat verschil is juridisch bepalend
  // (verwerken wij kinderdata, of blijft alles op het apparaat?), dus het hoort
  // afleesbaar te zijn in plaats van af te leiden uit gedrag.
  const [session, setSession] = useState<SessionState>(() => getSessionState());
  useEffect(() => subscribeToSession(setSession), []);

  // Wie kijkt er mee — rechtstreeks van de server, niet uit lokale staat.
  // Een kind moet dit kunnen zien op élk apparaat, ook waar de ouder nooit
  // heeft ingelogd.
  const [serverParents, setServerParents] = useState<{
    id: string;
    name: string;
  }[] | null>(null);
  useEffect(() => {
    if (session.status !== 'authenticated') return;
    if (session.profile.role !== 'learner') return;
    void fetchLinkedPeople().then(people => setServerParents(people.filter(p => p.relation === 'parent' || p.relation === 'teacher').map(p => ({
      id: p.id,
      name: p.name
    }))));
  }, [session]);

  // De koppelcode kwam uit de lokale opslag — een momentopname van het laatste
  // moment dat dít apparaat iets schreef. Twee dingen kunnen hem serverzijdig
  // wijzigen zonder dat dit apparaat het merkt: een beheerder die hem via break
  // glass vervangt, en het kind dat hem op een ander apparaat vervangt. In beide
  // gevallen leest het kind een code voor die nergens bij hoort, en krijgt de
  // ouder "code niet gevonden" — dagen later, bij iemand anders.
  //
  // Opnieuw proberen bij een mislukte profielophaling. Bewust een handmatige
  // knop en geen automatische herhaling: een lus die op de achtergrond blijft
  // proberen, verbergt precies het probleem dat de gebruiker moet zien.
  const [retryingProfile, setRetryingProfile] = useState(false);
  const handleRetryProfile = async () => {
    setRetryingProfile(true);
    try {
      await refreshProfile();
    } finally {
      setRetryingProfile(false);
    }
  };

  // `session.profile.linkCode` bevat de servercode óók, maar die is opgehaald
  // bij het aanmelden en verandert daarna niet meer binnen dezelfde sessie. Dit
  // scherm is precies het moment waarop het kind de code gaat voorlezen, dus
  // hier lezen we hem opnieuw.
  //
  // Lukt het ophalen niet, dan blijft de lokale waarde staan. Een leeg vak zou
  // een storing tot dataverlies maken.
  useEffect(() => {
    if (session.status !== 'authenticated') return;
    if (session.profile.role !== 'learner') return;
    const learnerId = session.profile.id;
    void fetchOwnLinkCode(learnerId).then(code => {
      if (code) setLearnerLinkCode(learnerId, code);
    });
  }, [session, setLearnerLinkCode]);

  // ── Afmelden op dit apparaat ───────────────────────────────────────
  // Gewoon uitloggen laat de leerdata op het apparaat staan. Dat is bewust —
  // wissen zou voor een solo-account dataverlies zijn, geen opschoning. Maar op
  // een geleend of gedeeld apparaat wil je juist wél schoon achterlaten. Dat
  // zijn twee verschillende wensen, dus twee knoppen.
  const [rosterOn, setRosterOn] = useState(() => isRosterEnabled());
  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [wipeError, setWipeError] = useState<string | null>(null);
  const syncsToServer = session.status === 'authenticated' && (session.profile.dataController === 'school' || session.profile.consentStatus === 'granted');
  const hasServerAccount = session.status === 'authenticated';
  const confirmWipeDevice = async () => {
    const learnerId = accounts?.activeAccountId;
    if (!learnerId) return;
    setWiping(true);
    setWipeError(null);

    // Dit staat BUITEN de try/finally hieronder, en dat is opzet: die finally
    // logt onvoorwaardelijk uit. Zat deze controle erbinnen, dan zou een
    // mislukte verzending het kind alsnog uitloggen — met een foutmelding die
    // het nooit te zien krijgt omdat het scherm al weg is.
    //
    // EERST de laatste voortgang naar de server duwen, en hier WEL afwachten.
    //
    // Bij gewoon uitloggen is dat bewust niet zo — daar mag niets blijven
    // hangen op een trage verbinding, en de data blijft lokaal staan als het
    // mislukt. Hier is dat precies andersom: we staan op het punt de lokale
    // kopie te vernietigen. Wat nu niet verstuurd is, bestaat straks nergens
    // meer. Het wegschrijven wacht vijf seconden na de laatste wijziging, dus
    // een kind dat klaar is en meteen opruimt zat altijd binnen dat venster.
    //
    // Dat maakte de belofte in de bevestiging onwaar: die zegt dat alles
    // terugkomt bij de volgende keer inloggen. Voor de laatste oefensessie
    // gold dat niet.
    if (syncsToServer) {
      const local = useStore.getState().multiAccountState?.learnerData?.[learnerId];
      if (local) {
        const ok = await pushSoloProgress(local as never);
        if (!ok) {
          // Wissen zonder bewaarde kopie is dataverlies, en daar heeft de
          // gebruiker niet om gevraagd. Dus stoppen, niets wissen, en zeggen
          // wat er aan de hand is.
          setWiping(false);
          setWipeConfirmOpen(false);
          setWipeError('Je laatste voortgang kon niet worden opgeslagen. Er is niets gewist. Controleer je internetverbinding en probeer het opnieuw.');
          return;
        }
      }
    }
    try {
      // Eerst de toetsgegevens (aparte opslag), dan het account met zijn
      // voortgang. In deze volgorde, want na het verwijderen van het account
      // is niet meer te bepalen wélke toetspogingen van dit kind waren.
      await deleteAssessmentDataForLearner(learnerId);
      deleteAccount(learnerId);
      // Ook de tegel weghalen. Blijft die staan, dan wijst hij naar gegevens
      // die er niet meer zijn — en suggereert hij dat er nog iets van dit kind
      // op dit apparaat staat.
      forgetProfile(learnerId);
    } finally {
      setWiping(false);
      setWipeConfirmOpen(false);
      onLogout?.();
    }
  };
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const confirmResetProgress = () => {
    const learnerId = viewingLearnerId || accounts?.activeAccountId;
    if (learnerId) {
      resetLearnerProgress(learnerId);
      onSetStartingPeriod(null);
    }
    setResetConfirmOpen(false);
  };
  const updatePref = <K extends keyof LearnerPreferences,>(key: K, value: LearnerPreferences[K]) => {
    setPrefs(prev => {
      const newPrefs = {
        ...prev,
        [key]: value
      };
      if (key === 'sound' || key === 'masterVolume' || key === 'voiceVolume' || key === 'celebrationVolume' || key === 'uiVolume') {
        // Refresh sound manager's cached on/off + volume mix immediately.
        setTimeout(() => soundManager.refreshPreference(), 0);
      }
      return newPrefs;
    });
  };
  // W2 — play a short preview of a category when its slider is committed, so
  // the child hears the effect of the change. Refresh the cache first (the
  // save effect has flushed localStorage by the time this fires), then play.
  const previewSound = (intent: SoundIntent) => {
    setTimeout(() => {
      soundManager.refreshPreference();
      setTimeout(() => playSoundIntent(intent), 70);
    }, 0);
  };
  const activeAccount = accounts ? accounts.accountsById[accounts.activeAccountId] : null;
  const isViewingLearner = !!viewingLearnerId && (activeAccount?.role === 'parent' || activeAccount?.role === 'teacher');
  const displayName = isViewingLearner ? state.stats.name : activeAccount?.displayName || state.stats.name;
  const accountRole = activeAccount?.role || 'learner';
  const roleLabel = getAccountRoleLabel(accountRole);
  const isLearner = accountRole === 'learner';
  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(displayName);
  useEffect(() => {
    setEditedName(displayName);
  }, [displayName]);
  const handleSaveName = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== displayName && onUpdateName) {
      onUpdateName(trimmedName);
      setIsEditingName(false);
    } else {
      setEditedName(displayName);
      setIsEditingName(false);
    }
  };
  const handleCancelEdit = () => {
    setEditedName(displayName);
    setIsEditingName(false);
  };
  // Avatar upload handling
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 200;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        updatePref('avatarUrl', dataUrl);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
    // Reset input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleRemoveAvatar = () => {
    updatePref('avatarUrl', undefined);
  };
  // Get linked parents and teachers for the current learner
  const linkedInfo = useMemo(() => {
    if (!accounts || accountRole !== 'learner') return null;
    const learnerId = accounts.activeAccountId;
    if (!learnerId) return null;
    const learnerAccount = accounts.accountsById[learnerId];
    if (!learnerAccount || !isLearnerAccount(learnerAccount)) return null;
    // Get linked parents
    const parentIds = accounts.relationships.learnerToParents[learnerId] || [];
    const parents = parentIds.map(id => accounts.accountsById[id]).filter(Boolean).map(a => ({
      name: a.displayName,
      role: 'Ouder' as const
    }));
    // Get linked teachers
    const teachers: {
      name: string;
      role: 'Docent';
      className?: string;
    }[] = [];
    Object.entries(accounts.relationships.teacherToLearners).forEach(([teacherId, learnerIds]) => {
      if (learnerIds.includes(learnerId)) {
        const teacher = accounts.accountsById[teacherId];
        if (teacher && isTeacherAccount(teacher)) {
          const teacherClassroom = Object.values(accounts.classrooms ?? {}).find(c => c.teacherId === teacherId && c.learnerIds.includes(learnerId));
          teachers.push({
            name: teacher.displayName,
            role: 'Docent',
            className: teacherClassroom?.name
          });
        }
      }
    });
    return {
      parents,
      teachers,
      linkCode: learnerAccount.linkCode,
      learnerId
    };
  }, [accounts, accountRole]);
  // Copy link code state
  const [codeCopied, setCodeCopied] = useState(false);
  const handleCopyCode = () => {
    if (linkedInfo?.linkCode) {
      navigator.clipboard.writeText(linkedInfo.linkCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  // Supportnummer — apart van `codeCopied`, zodat de bevestiging bij de juiste
  // code verschijnt. Eén gedeelde vlag zou het vinkje ook bij de koppelcode laten
  // oplichten, en dat is precies de verwarring die deze twee codes al hebben.
  // Koppelcode vervangen (server-first). De lokale kopie wordt pas bijgewerkt
  // nadat de server een nieuwe code heeft teruggegeven — anders toont het scherm
  // een code die nergens bestaat, precies de fout die het registratiescherm had.
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);
  const [renewedCode, setRenewedCode] = useState<string | null>(null);
  const handleRenewLinkCode = async () => {
    if (!linkedInfo?.learnerId) return;
    setRenewBusy(true);
    setRenewError(null);
    const res = await regenerateOwnLinkCode();
    setRenewBusy(false);
    if (!res.ok || !res.code) {
      setRenewError(res.message ?? 'Er ging iets mis. Je oude code werkt gewoon nog.');
      return;
    }
    setLearnerLinkCode(linkedInfo.learnerId, res.code);
    setRenewedCode(res.code);
    setRenewOpen(false);
  };
  const [supportCodeCopied, setSupportCodeCopied] = useState(false);
  const handleCopySupportCode = () => {
    if (!linkedInfo?.learnerId) return;
    navigator.clipboard.writeText(pseudonymFor(linkedInfo.learnerId));
    setSupportCodeCopied(true);
    setTimeout(() => setSupportCodeCopied(false), 2000);
  };
  // Join Classroom State
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const handleJoinClassroom = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) {
      setJoinError('Een klascode bestaat uit 6 tekens.');
      return;
    }
    setJoinLoading(true);
    setJoinError(null);
    try {
      const result = await joinClassroomByCode(code);
      if (result.success) {
        setJoinSuccess(true);
        setJoinCode('');
      } else {
        setJoinError(result.error || 'Klas niet gevonden of al lid.');
      }
    } catch (e) {
      setJoinError('Er is een fout opgetreden bij het koppelen.');
    } finally {
      setJoinLoading(false);
    }
  };
  const dailyGoalOptions: {
    value: 5 | 10 | 20;
    emoji: string;
    label: string;
  }[] = [{
    value: 5,
    emoji: '🌱',
    label: 'Rustig'
  }, {
    value: 10,
    emoji: '⭐',
    label: 'Standaard'
  }, {
    value: 20,
    emoji: '🚀',
    label: 'Uitdaging'
  }];
  const weeklyGoalOptions: {
    value: 3 | 5 | 7;
    emoji: string;
    label: string;
  }[] = [{
    value: 3,
    emoji: '🥉',
    label: 'Basis'
  }, {
    value: 5,
    emoji: '🥈',
    label: 'Actief'
  }, {
    value: 7,
    emoji: '🥇',
    label: 'Elke dag'
  }];
  const periodOptions: {
    value: StartingPeriod | null;
    emoji: string | React.ReactNode;
    label: string;
    fullLabel: string;
  }[] = [{
    value: null,
    emoji: <Sparkles className="w-6 h-6 mx-auto text-emerald-400" data-id="element-2116" data-mp="25rayl7r5w8ly" />,
    label: 'Auto',
    fullLabel: 'Automatisch'
  }, {
    value: 'B',
    emoji: '🌱',
    label: 'Begin',
    fullLabel: 'Begin schooljaar'
  }, {
    value: 'M',
    emoji: '☀️',
    label: 'Midden',
    fullLabel: 'Midden schooljaar'
  }, {
    value: 'E',
    emoji: '🎓',
    label: 'Eind',
    fullLabel: 'Eind schooljaar'
  }];
  const hasMasteryData = Object.keys(state.familyMastery || {}).length > 0;
  return <PageContainer data-id="element-2117" data-mp="2dkbvzhs5ypy7">
      <motion.div variants={itemVariants} data-id="element-2118" data-mp="2dktxastonucz">
        <PageHero title="Instellingen" subtitle="Pas je profiel en voorkeuren aan" gradientClasses="from-orange-500 via-amber-500 to-yellow-500" icon={Settings} iconBgClass="bg-orange-400" djayEmotion="happy" djayVariant={selectedVariant} floatingEmojis={['✨', '🎨']} data-id="element-2119" data-mp="2dm53ezalpe79" />
        
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-2120" data-mp="2rlej7xuwa6ap">
        <WorldZone title="Profiel" icon={User} accentColor="bg-orange-100 text-orange-600" isLast={false} data-id="element-2121" data-mp="2wjfys8t43nhf">
          
          <Card className="p-8 md:p-10" data-id="element-2122" data-mp="30jzwpeliev4m">
            <div className="flex flex-col sm:flex-row items-center gap-8" data-id="element-2123" data-mp="30iyix2d3zuj7">
              {/* Avatar */}
              <div className="flex flex-col items-center gap-3" data-id="element-2124" data-mp="35dkxtfieg581">
                <div className="relative flex-shrink-0 cursor-pointer group" onClick={() => fileInputRef.current?.click()} data-id="element-2125" data-mp="3a99qcyw04luu">
                  
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleAvatarUpload} data-id="element-2126" data-mp="3f7urd0j2v9kc" />
                  
                  <div className="w-28 h-28 rounded-full flex items-center justify-center text-white text-5xl font-bold shadow-lg shadow-orange-200/50 bg-gradient-to-br from-orange-400 to-amber-500 overflow-hidden transition-transform group-hover:scale-105" data-id="element-2127" data-mp="18mglb8thl0iq">
                    {prefs.avatarUrl ? <img src={prefs.avatarUrl} alt="Avatar" className="w-full h-full object-cover" data-id="element-2128" data-mp="too84nbewo7p" /> : displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-2 shadow-md border border-slate-100 text-slate-400 group-hover:text-orange-500 group-hover:border-orange-200 transition-colors" data-id="element-2129" data-mp="1rdzll7w4m0h2">
                    <Camera className="w-5 h-5" data-id="element-2130" data-mp="1cn0iudhz265e" />
                  </div>
                  <div className="absolute -top-2 -left-2 bg-white rounded-full p-1 shadow-sm z-10" data-id="element-2131" data-mp="1cmqsupwxfyzp">
                    <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-2132" data-mp="2a3pka630f6e9">
                      Lvl {state.stats.level}
                    </div>
                  </div>
                </div>
                {prefs.avatarUrl && <button onClick={handleRemoveAvatar} className="text-xs text-rose-500 hover:text-rose-600 font-medium flex items-center gap-1" data-id="element-2133" data-mp="8dqmmqsnq11o">
                  
                    <Trash2 className="w-3 h-3" data-id="element-2134" data-mp="3prm8tyho2tcc" />
                    Verwijder foto
                  </button>}
              </div>

              {/* Info */}
              <div className="flex-1 text-center sm:text-left" data-id="element-2135" data-mp="3grz6nah45ji8">
                {/* Name with Edit */}
                {isEditingName ? <div className="flex items-center gap-3 mb-3 justify-center sm:justify-start" data-id="element-2136" data-mp="3kw6xbd74t9j3">
                    <Input type="text" value={editedName} onChange={e => setEditedName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEdit();
                }} className="text-3xl font-bold text-slate-900 border-2 border-orange-300 rounded-lg px-4 py-2 focus:border-orange-500 min-h-[56px] max-w-[200px]" autoFocus maxLength={30} fullWidth={false} data-id="element-2137" data-mp="36wfg73lbjd0v" />
                  
                    <button onClick={handleSaveName} className="p-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-emerald-300 flex items-center justify-center" title="Opslaan" data-id="element-2138" data-mp="2pmbky73hagqw">
                    
                      <Save className="w-5 h-5" data-id="element-2139" data-mp="3dabgrlfncxcc" />
                    </button>
                    <button onClick={handleCancelEdit} className="p-3 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-slate-300 flex items-center justify-center" title="Annuleren" data-id="element-2140" data-mp="38c9yv3tyj42g">
                    
                      <X className="w-5 h-5" data-id="element-2141" data-mp="1suzwokgp5eoa" />
                    </button>
                  </div> : <div className="flex items-center gap-4 mb-3 justify-center sm:justify-start" data-id="element-2142" data-mp="1nw6ewfudn1ib">
                    <h2 className="text-3xl font-black text-slate-900" data-id="element-2143" data-mp="ez897wf4zq90">
                      {displayName}
                    </h2>
                    {onUpdateName && <button onClick={() => setIsEditingName(true)} className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors min-h-[40px] min-w-[40px] focus:outline-none focus:ring-2 focus:ring-orange-300 flex items-center justify-center" title="Naam wijzigen" data-id="element-2144" data-mp="jx1e8m4pee7p">
                    
                        <Edit2 className="w-5 h-5" data-id="element-2145" data-mp="3sc1k3owygg1c" />
                      </button>}
                  </div>}

                <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-3" data-id="element-2146" data-mp="15qs0i9xapm5">
                  <Badge variant="info" icon={<School className="w-4 h-4" data-id="element-2148" data-mp="a055qget1s35" />} size="lg" data-id="element-2147" data-mp="57bxvjo1i3mu">
                    
                    Groep {selectedGroup}
                  </Badge>
                  <Badge variant="default" icon={<Shield className="w-4 h-4" data-id="element-2150" data-mp="39lty4jle068d" />} size="lg" data-id="element-2149" data-mp="39mv6ggb0qeme">
                    
                    {roleLabel}
                  </Badge>
                </div>

                {/* XP Progress */}
                <div className="mt-6 max-w-xs mx-auto sm:mx-0" data-id="element-2151" data-mp="363mmf2r5gbj6">
                  <div className="flex items-center justify-between mb-2" data-id="element-2152" data-mp="317xkwtvkkkk8">
                    <span className="text-sm font-semibold text-slate-600" data-id="element-2153" data-mp="2x49fagqhzz1m">
                      Level {state.stats.level}
                    </span>
                    <span className="text-sm font-bold text-orange-600" data-id="element-2154" data-mp="2s824oan5pzdc">
                      {state.stats.xp} XP
                    </span>
                  </div>
                  <ProgressBar progress={getProgressToNextLevel(state.stats.xp).percent} color="orange" size="md" animated data-id="element-2155" data-mp="2ifcwppqtf3mr" />
                  
                  <p className="text-xs text-slate-500 mt-2 text-center sm:text-left" data-id="element-2156" data-mp="2dep6h6aeuqtl">
                    Nog{' '}
                    {getProgressToNextLevel(state.stats.xp).needed - getProgressToNextLevel(state.stats.xp).current}{' '}
                    XP tot Level {state.stats.level + 1}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </WorldZone>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-2157" data-mp="1utapazo389xr">
        <WorldZone title="Voorkeuren" icon={Sliders} accentColor="bg-amber-100 text-amber-600" isLast={false} data-id="element-2158" data-mp="1uuc8jrf9c2qk">
          
          <div className="space-y-2" data-id="element-2159" data-mp="1fxv0b92d7488">
            {/* Sound Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-white/50 transition-colors" data-id="element-2160" data-mp="1kv3j9bvc7caj">
              <div className="flex items-center gap-4" data-id="element-2161" data-mp="28m8z3r1a24ku">
                <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600 shadow-sm border border-sky-100" data-id="element-2162" data-mp="2di7i0z3u6udv">
                  <Volume2 className="w-5 h-5" data-id="element-2163" data-mp="1ysa19488l6yp" />
                </div>
                <div data-id="element-2164" data-mp="23s4pf00mnig2">
                  <div className="font-bold text-slate-900" data-id="element-2165" data-mp="23rcwjwyezyth">
                    Geluidseffecten
                  </div>
                  <div className="text-sm text-slate-600" data-id="element-2166" data-mp="2w9qm799lkehh">
                    Geluidjes bij goede antwoorden en beloningen
                  </div>
                </div>
              </div>
              <Toggle checked={prefs.sound} onChange={v => updatePref('sound', v)} label="Geluidseffecten" variant="student" data-id="element-2167" data-mp="ksey96t3gr0z" />
              
            </div>

            {/* Sound Mix (App-Feel 2027 W2) */}
            <div className={`px-4 pb-4 transition-opacity ${prefs.sound ? 'opacity-100' : 'opacity-40 pointer-events-none select-none'}`} aria-hidden={!prefs.sound} data-id="element-2168" data-mp="plhujmp9aekv">
              
              <div className="rounded-2xl bg-white/60 border border-slate-100 p-5 space-y-5" data-id="element-2169" data-mp="1y00tlmfl6f9">
                <div className="text-sm font-semibold text-slate-500" data-id="element-2170" data-mp="3t4b65tyh4tce">
                  Geluidsbalans
                </div>
                {SOUND_MIX_SLIDERS.map(slider => {
                const value = prefs[slider.key] as number | undefined ?? 1;
                return <div key={slider.key} data-id="element-2171" data-mp="1mapit2h9f1sh">
                      <div className="flex items-center justify-between mb-1.5" data-id="element-2172" data-mp="1mdtsugheorr2">
                        <div data-id="element-2173" data-mp="1i9vu46vztiyj">
                          <span className="font-bold text-slate-900 text-sm" data-id="element-2174" data-mp="3bsw5yubgcj9q">
                            {slider.label}
                          </span>
                          <span className="block text-xs text-slate-500" data-id="element-2175" data-mp="3gsqx8yz53avb">
                            {slider.hint}
                          </span>
                        </div>
                        <span className={`text-sm font-bold tabular-nums ${ACCENT_TEXT[slider.accent]}`} data-id="element-2176" data-mp="3qi0lu4urybeo">
                          
                          {Math.round(value * 100)}%
                        </span>
                      </div>
                      <input type="range" min={0} max={1} step={0.05} value={value} disabled={!prefs.sound} onChange={e => updatePref(slider.key, Number(e.target.value))} onMouseUp={() => previewSound(slider.preview)} onTouchEnd={() => previewSound(slider.preview)} onKeyUp={() => previewSound(slider.preview)} aria-label={slider.label} className={`w-full h-2 rounded-full cursor-pointer bg-slate-200 ${ACCENT_RANGE[slider.accent]}`} data-id="element-2177" data-mp="3f5o1nwca990" />
                      
                    </div>;
              })}
              </div>
            </div>

            {/* Reduced Motion Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-white/50 transition-colors" data-id="element-2178" data-mp="rf7o8b53xlyw">
              <div className="flex items-center gap-4" data-id="element-2179" data-mp="nfff9xbsxdt0">
                <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 shadow-sm border border-purple-100" data-id="element-2180" data-mp="igvqzq9qjpxh">
                  <Sparkles className="w-5 h-5" data-id="element-2181" data-mp="1f40o2iouodys" />
                </div>
                <div data-id="element-2182" data-mp="1b59om4wwwk8i">
                  <div className="font-bold text-slate-900" data-id="element-2183" data-mp="1b2nepymjtsn4">
                    Animaties verminderen
                  </div>
                  <div className="text-sm text-slate-600" data-id="element-2184" data-mp="116u6grfv4lb5">
                    Rustiger beeld zonder bewegende effecten
                  </div>
                  <div className="text-xs text-purple-600 font-medium mt-0.5" data-id="element-2185" data-mp="3qwp929jpt9gn">
                    Wordt actief na herladen
                  </div>
                </div>
              </div>
              <Toggle checked={prefs.reducedMotion} onChange={v => updatePref('reducedMotion', v)} label="Animaties verminderen" variant="student" data-id="element-2186" data-mp="2k681a0sm8fgv" />
              
            </div>

            <div className="p-4 rounded-2xl flex items-center justify-between" data-id="element-2187" data-mp="2sxqjeh94h5cl">
              <div className="flex items-center gap-4" data-id="element-2188" data-mp="2o70et68it6iy">
                <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm border border-indigo-100" data-id="element-2189" data-mp="2o5gwzb77unf2">
                  <Sparkles className="w-5 h-5" data-id="element-2190" data-mp="2xx36n33woefp" />
                </div>
                <div data-id="element-2191" data-mp="2xyxog8jvrqfk">
                  <div className="font-bold text-slate-900" data-id="element-2192" data-mp="3bwuppi9a4l0k">Rondleiding</div>
                  <div className="text-sm text-slate-600" data-id="element-2193" data-mp="3bu02xk2smo3z">
                    Bekijk de uitleg over de app opnieuw
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={() => {
              updatePref('pendingAppTour', true);
              updatePref('tourCompleted', false);
              window.location.reload();
            }} data-id="element-2194" data-mp="1hkeavt2f3yk3">
                
                Start
              </Button>
            </div>

            {/* Daily Goal Selector */}
            <div className="p-4 rounded-2xl" data-id="element-2195" data-mp="tn87urxob6hl">
              <div className="flex items-center gap-4 mb-5" data-id="element-2196" data-mp="tmgocfhcoz7o">
                <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shadow-sm border border-amber-100" data-id="element-2197" data-mp="yljhb1k23eza">
                  <Target className="w-5 h-5" data-id="element-2198" data-mp="jnheitkumxj3" />
                </div>
                <div data-id="element-2199" data-mp="ooyb4cwavxtv">
                  <div className="font-bold text-slate-900" data-id="element-2200" data-mp="op6r4dnpzj9a">Dagelijks doel</div>
                  <div className="text-sm text-slate-600" data-id="element-2201" data-mp="apfhsq85er1m">
                    Hoeveel vragen wil je per dag oefenen?
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-2202" data-mp="251vmhmlapx4d">
                {dailyGoalOptions.map(opt => <button key={opt.value} onClick={() => updatePref('dailyGoal', opt.value)} className={`
                      relative py-5 px-3 rounded-2xl border-2 font-bold transition-all text-center
                      ${prefs.dailyGoal === opt.value ? 'border-amber-400 bg-amber-50 text-amber-800 shadow-[0_2px_0_0_rgb(251,191,36)]' : 'border-white/60 bg-white/40 text-slate-600 hover:border-amber-200 hover:bg-amber-50/50'}
                    `} aria-pressed={prefs.dailyGoal === opt.value} data-id="element-2203" data-mp="2a1rppjm65kiv">
                  
                    <div className="text-2xl mb-1" data-id="element-2204" data-mp="2r9iwdk3qx86t">{opt.emoji}</div>
                    <div className="text-xl" data-id="element-2205" data-mp="2mbpqks6cug72">{opt.value}</div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5" data-id="element-2206" data-mp="2mc96y4gcwwiz">
                      {opt.label}
                    </div>
                  </button>)}
              </div>
            </div>

            {/* Weekly Goal Selector */}
            <div className="p-4 rounded-2xl" data-id="element-2207" data-mp="3a1k296extypq">
              <div className="flex items-center gap-4 mb-5" data-id="element-2208" data-mp="3a2d0fiztzfw9">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 shadow-sm border border-emerald-100" data-id="element-2209" data-mp="35a1yybpfzm1l">
                  <Calendar className="w-5 h-5" data-id="element-2210" data-mp="3cnuiokfinwi6" />
                </div>
                <div data-id="element-2211" data-mp="3hl320ocei6l0">
                  <div className="font-bold text-slate-900" data-id="element-2212" data-mp="3hmnnlogqmf6q">Weekdoel</div>
                  <div className="text-sm text-slate-600" data-id="element-2213" data-mp="3mi2xy6fjdsye">
                    Hoeveel dagen per week wil je oefenen?
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-2214" data-mp="3vjax0qpvmxtj">
                {weeklyGoalOptions.map(opt => <button key={opt.value} onClick={() => updatePref('weeklyGoal', opt.value)} className={`
                      relative py-5 px-3 rounded-2xl border-2 font-bold transition-all text-center
                      ${prefs.weeklyGoal === opt.value ? 'border-emerald-400 bg-emerald-50 text-emerald-800 shadow-[0_2px_0_0_rgb(52,211,153)]' : 'border-white/60 bg-white/40 text-slate-600 hover:border-emerald-200 hover:bg-emerald-50/50'}
                    `} aria-pressed={prefs.weeklyGoal === opt.value} data-id="element-2215" data-mp="4d9gi2n57c0i">
                  
                    <div className="text-2xl mb-1" data-id="element-2216" data-mp="1krub5xv914au">{opt.emoji}</div>
                    <div className="text-xl" data-id="element-2217" data-mp="1fv3wnez3egrj">{opt.value}</div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5" data-id="element-2218" data-mp="1tlpkli3elgwa">
                      {opt.label}
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
        </WorldZone>
      </motion.div>

      {isLearner && <motion.div variants={itemVariants} data-id="element-2219" data-mp="11ut8wkt2xial">
          <WorldZone title="Instapmoment" icon={Calendar} accentColor="bg-teal-100 text-teal-600" isLast={false} data-id="element-2220" data-mp="wvzsokmerd6q">
          
            <div className="space-y-4" data-id="element-2221" data-mp="1awahzj82kjps">
              <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2222" data-mp="1at4xkhady60e">
                <div className="flex items-start gap-4 mb-5" data-id="element-2223" data-mp="15y91n6212yi0">
                  <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 shadow-sm border border-teal-100 flex-shrink-0" data-id="element-2224" data-mp="2m9d3msp0zzgt">
                    <Calendar className="w-5 h-5" data-id="element-2225" data-mp="2psk7g1ooeklb" />
                  </div>
                  <div data-id="element-2226" data-mp="2pueupmnf6osk">
                    <div className="font-bold text-slate-900" data-id="element-2227" data-mp="2ttnnopamyin2">
                      Kies je startmoment
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-2228" data-mp="2fv61wx4ajg7s">
                      Bepaalt welke stof als eerste wordt aangeboden. Standaard
                      past dit zich automatisch aan het schooljaar aan.
                    </div>
                  </div>
                </div>

                {hasMasteryData && <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-id="element-2229" data-mp="3dgv56vz2s45d">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-2230" data-mp="2ysh0sgm0g7mp" />
                    <div className="text-sm text-amber-800" data-id="element-2231" data-mp="33qs9up4hee3v">
                      Je hebt al geoefend. Om je startmoment opnieuw in te
                      stellen, moet je eerst je voortgang wissen.
                      <button onClick={() => setResetConfirmOpen(true)} className="block mt-2 text-amber-600 font-bold hover:text-amber-700 underline" data-id="element-2232" data-mp="4kxfe28zy70">
                    
                        Voortgang wissen
                      </button>
                    </div>
                  </div>}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-2233" data-mp="3w4vrkvktgog9">
                  {periodOptions.map(opt => <button key={opt.value || 'auto'} onClick={() => onSetStartingPeriod(opt.value)} disabled={hasMasteryData} className={`
                        relative py-4 px-2 rounded-2xl border-2 font-bold transition-all text-center flex flex-col items-center justify-center
                        ${(manualPeriod ?? null) === opt.value ? 'border-teal-400 bg-teal-50 text-teal-800 shadow-[0_2px_0_0_rgb(45,212,191)]' : 'border-slate-200 bg-white text-slate-600 hover:border-teal-200 hover:bg-teal-50/50'}
                        ${hasMasteryData ? 'opacity-50 cursor-not-allowed' : ''}
                      `} aria-pressed={(manualPeriod ?? null) === opt.value} title={opt.fullLabel} data-id="element-2234" data-mp="3s3tsceqwgcea">
                  
                      <div className="text-2xl mb-1 flex items-center justify-center h-8" data-id="element-2235" data-mp="1exm0jp2nfymw">
                        {opt.emoji}
                      </div>
                      <div className="text-sm font-semibold mt-1" data-id="element-2236" data-mp="1ny24t0r7v9st">
                        {opt.label}
                      </div>
                    </button>)}
                </div>

                {(manualPeriod ?? null) === null && <div className="mt-4 text-sm text-center text-slate-500 font-medium" data-id="element-2237" data-mp="w8fd5oqwuiur">
                    Huidig automatisch moment:{' '}
                    <span className="text-teal-600 font-bold" data-id="element-2238" data-mp="106woylla0hii">
                      {getAutoPhaseLabel()}
                    </span>
                  </div>}
              </div>
            </div>
          </WorldZone>
        </motion.div>}

      {isLearner && <motion.div variants={itemVariants} data-id="element-2239" data-mp="ck6ldfta4evs">
          <WorldZone title="Koppelcode & Privacy" icon={Shield} accentColor="bg-blue-100 text-blue-600" isLast={false} data-id="element-2240" data-mp="chc3nw1nh862">
          
            <div className="space-y-6" data-id="element-2241" data-mp="370be6u9jgs2x">
              {/* Klascode invoeren (only if no teacher linked) */}
              {linkedInfo && linkedInfo.teachers.length === 0 && !joinSuccess && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2242" data-mp="323tdc5hbxdvv">
                    <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2243" data-mp="3fz5aejiyjgfy">
                      <Users className="w-5 h-5 text-blue-500" data-id="element-2244" data-mp="3c20scis25lv8" />
                      Klascode invoeren
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2245" data-mp="3l0ulfz66ju41">
                      Heb je een code gekregen van je leraar? Vul hem hier in om
                      je aan te sluiten bij de klas.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3" data-id="element-2246" data-mp="3urgtn8bkhicp">
                      <Input type="text" value={joinCode} onChange={e => {
                setJoinCode(e.target.value.toUpperCase());
                setJoinError(null);
              }} placeholder={`Bijv. ${JOIN_CODE_EXAMPLE}`} maxLength={JOIN_CODE_LENGTH} className="bg-slate-50 border-slate-200 py-3 font-mono text-lg font-bold text-slate-900 focus:ring-blue-500 uppercase" data-id="element-2247" data-mp="cjzp72jw17yx" />
                
                      <Button onClick={handleJoinClassroom} disabled={joinCode.trim().length !== JOIN_CODE_LENGTH || joinLoading} className="py-3 px-6" data-id="element-2248" data-mp="i731khm2jn8c">
                  
                        {joinLoading ? 'Koppelen...' : 'Koppelen'}
                      </Button>
                    </div>
                    {joinError && <p className="text-sm text-rose-500 mt-3 font-medium flex items-center gap-1.5" data-id="element-2249" data-mp="d7qcrrbn0634">
                        <AlertCircle className="w-4 h-4" data-id="element-2250" data-mp="3uozvhqmmqddb" />
                        {joinError}
                      </p>}
                  </div>}

              {/* Success message after joining */}
              {joinSuccess && <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100 shadow-sm flex items-center gap-4" data-id="element-2251" data-mp="3lol7ukm70m6n">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-2252" data-mp="3qljy2s2qk8zk">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" data-id="element-2253" data-mp="lkk5kq4o4d60" />
                  </div>
                  <div data-id="element-2254" data-mp="plm3n3mumj6e">
                    <h3 className="font-bold text-emerald-900 text-lg" data-id="element-2255" data-mp="bswguwzhxa88">
                      Gekoppeld!
                    </h3>
                    <p className="text-emerald-700 font-medium" data-id="element-2256" data-mp="gq6f924v4w87">
                      Je bent succesvol toegevoegd aan de klas.
                    </p>
                  </div>
                </div>}

              {/* Koppelcode */}
              {linkedInfo?.linkCode && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2257" data-mp="3oywnjg3zme9n">
                  <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2258" data-mp="3txzs6zg22ih3">
                    <LinkIcon className="w-5 h-5 text-blue-500" data-id="element-2259" data-mp="1n7jrlgjzzgpq" />
                    Mijn Koppelcode
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2260" data-mp="1s762vkg9mmvw">
                    Deel deze code met je ouder(s) zodat zij je voortgang kunnen
                    volgen.
                  </p>
                  <div className="flex items-center gap-3" data-id="element-2261" data-mp="1xc9o36wmtsmk">
                    <div className="bg-blue-50 border border-blue-100 px-5 py-2.5 rounded-xl font-mono text-xl font-black tracking-widest text-blue-900" data-id="element-2262" data-mp="1x9ngj79qrd4c">
                      {linkedInfo.linkCode}
                    </div>
                    <button onClick={handleCopyCode} className="p-2.5 bg-blue-50 border border-blue-100 rounded-xl text-blue-600 hover:bg-blue-100 transition-all" title="Kopieer code" data-id="element-2263" data-mp="2l0jfcocm78l3">
                  
                      {codeCopied ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-2264" data-mp="2u0poxz56zoez" /> : <Copy className="w-5 h-5" data-id="element-2265" data-mp="2tzej7ts6s6l8" />}
                    </button>
                    {codeCopied && <span className="text-sm font-bold text-emerald-600" data-id="element-2266" data-mp="2pyb79id9eupa">
                        Gekopieerd!
                      </span>}
                  </div>

                  {/*
                    ─── ZELF EEN NIEUWE CODE ────────────────────────────────
                    Deelt een kind zijn code te breed — in een klasgroep, met
                    een vriendje, per ongeluk in beeld tijdens een videogesprek
                    — dan is dat kind degene die het weet en degene die er last
                    van heeft. Tot nu toe kon alléén een beheerder vervangen,
                    via break glass. Dan gebeurt het niet, en blijft de code
                    permanent geldig.
                     De bevestigingsstap is niet decoratief: dit is de enige
                    knop in dit scherm die iets kapotmaakt wat het kind al aan
                    iemand heeft gegeven. Zelfde vorm als bij de herstelcode,
                    zodat de twee codes zich hetzelfde gedragen.
                     Al gekoppelde ouders blijven gekoppeld — dat staat er
                    expliciet bij, want zonder die zin durft een kind de knop
                    niet te gebruiken uit angst papa kwijt te raken.
                   */}
                  {!renewOpen && !renewedCode && <button onClick={() => {
              setRenewOpen(true);
              setRenewError(null);
            }} className="mt-4 text-sm font-bold text-slate-500 underline underline-offset-2 transition-colors hover:text-blue-600" data-id="element-2267" data-mp="1ptj3d6uynxfv">
                      Iemand heeft mijn code die hem niet mag hebben
                    </button>}

                  {renewOpen && <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-id="element-2268" data-mp="29g8evp6xrqd2">
                      <p className="text-sm font-bold text-amber-900" data-id="element-2269" data-mp="24gccfudw06zm">
                        Wil je een nieuwe code?
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-amber-900/80" data-id="element-2270" data-mp="1zob2wg83suda">
                        Je oude code werkt daarna niet meer, ook niet als je hem
                        al aan iemand hebt gegeven. Ouders die al gekoppeld zijn,
                        blijven gewoon gekoppeld.
                      </p>
                      {renewError && <p className="mt-3 text-sm font-medium text-rose-700" data-id="element-2271" data-mp="27xyowept2945">
                          {renewError}
                        </p>}
                      <div className="mt-3 flex flex-wrap gap-2" data-id="element-2272" data-mp="2c03ijdg975qs">
                        <button onClick={handleRenewLinkCode} disabled={renewBusy} className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-amber-700 disabled:opacity-60" data-id="element-2273" data-mp="1y02cljl4t84g">
                          {renewBusy ? 'Bezig…' : 'Ja, maak een nieuwe code'}
                        </button>
                        <button onClick={() => setRenewOpen(false)} disabled={renewBusy} className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-bold text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-60" data-id="element-2274" data-mp="2vlzsrac1h1d8">
                          Nee, laat maar
                        </button>
                      </div>
                    </div>}

                  {renewedCode && <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-id="element-2275" data-mp="3eeu7q49nwthg">
                      <p className="text-sm font-bold text-emerald-900" data-id="element-2276" data-mp="12xs2d4bvkawm">
                        Je hebt een nieuwe code
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-emerald-900/80" data-id="element-2277" data-mp="xxw9a04pu3vu">
                        Hij staat hierboven. Geef deze aan je ouder(s); de oude
                        werkt niet meer.
                      </p>
                    </div>}
                </div>}

              {/*
                 Supportnummer — bewust GEEN derde codekaart.
                 
                 Er staan hier al twee codes met tegengestelde regels (de
                 koppelcode déél je, de herstelcode juist niet). Een derde blok in
                 dezelfde vorm maakt die twee minder duidelijk, en dat is een
                 slechtere ruil dan het waard is. Daarom klein, grijs en
                 onderaan: dit is niets om te onthouden of te bewaren, alleen om
                 op te zoeken als iemand ernaar vraagt.
                 
                 WAAROM HET BESTAAT: een beheerder ziet leerlingen alleen als
                 pseudonym (L-XXXXX), want namen worden niet meegestuurd. Meldt
                 een school "Klaasje heeft een probleem", dan is er zonder dit
                 nummer geen enkele manier om de juiste leerling te vinden —
                 behalve gokken, of de afscherming omzeilen. Het nummer is uit het
                 account-id afgeleid en bevat zelf geen persoonsgegevens.
                */}
              {linkedInfo?.learnerId && <div className="px-5 py-4 bg-slate-50 rounded-2xl border border-slate-100" data-id="element-2278" data-mp="iffc60fir9u4">
                  <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-2279" data-mp="ncflqlbkuupz">
                    <div data-id="element-2280" data-mp="s4guhx9je3b9">
                      <h3 className="text-sm font-bold text-slate-700" data-id="element-2281" data-mp="s4ywlaivrbr3">
                        Supportnummer
                      </h3>
                      <p className="mt-0.5 text-xs text-slate-500" data-id="element-2282" data-mp="3qscvit5tu0ns">
                        Alleen nodig als je hulp vraagt. Je hoeft dit niet te
                        onthouden.
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2283" data-mp="3jaerv5abtjsh">
                      <span className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 font-mono text-sm font-bold tracking-wider text-slate-700" data-id="element-2284" data-mp="3j8vcdgwhvcrr">
                        {pseudonymFor(linkedInfo.learnerId)}
                      </span>
                      <button onClick={handleCopySupportCode} className="p-2 rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-100" title="Kopieer supportnummer" data-id="element-2285" data-mp="35bymbho79zbx">
                        {supportCodeCopied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-2286" data-mp="2vln914immdsd" /> : <Copy className="w-4 h-4" data-id="element-2287" data-mp="2qmiwc3whtxxa" />}
                      </button>
                    </div>
                  </div>
                </div>}

              {/* Herstelcode — direct ná de koppelcode, want dat is precies
                 de plek waar de verwarring zit: twee codes die op elkaar
                 lijken en tegengestelde regels hebben. De koppelcode déél je,
                 de herstelcode juist niet. Ze naast elkaar zetten met dat
                 verschil erbij is duidelijker dan ze verstoppen. */}
              <RecoveryCodeSection data-id="element-2288" data-mp="b6dapa6ztxf" />

              {/* Wie kan mijn voortgang zien? */}
              {linkedInfo && (linkedInfo.parents.length > 0 || linkedInfo.teachers.length > 0) && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2289" data-mp="3mr0uojksgcty">
                    <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2290" data-mp="3rirav66eputa">
                      <Eye className="w-5 h-5 text-emerald-500" data-id="element-2291" data-mp="3ctv3zcg36nu9" />
                      Wie kan mijn voortgang zien?
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2292" data-mp="3hszafvbmm7gw">
                      Deze mensen kunnen je scores bekijken, maar niet je
                      wachtwoord of instellingen wijzigen.
                    </p>
                    <div className="space-y-4" data-id="element-2293" data-mp="2p5y9b1em7py0">
                      {linkedInfo.parents.length > 0 && <div data-id="element-2294" data-mp="2p82cfh4owkx8">
                          <div className="flex items-center gap-2 mb-2" data-id="element-2295" data-mp="2k74v16ri7uws">
                            <Heart className="w-4 h-4 text-rose-500" data-id="element-2296" data-mp="2k97u0gamwydn" />
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-2297" data-mp="2y87hvlqbp98r">
                              Ouders / Verzorgers
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-2298" data-mp="382r10t84wiq8">
                            {linkedInfo.parents.map((p, i) => <div key={i} className="flex items-center gap-2 bg-rose-50 rounded-lg px-3 py-1.5 border border-rose-100" data-id="element-2299" data-mp="333mu698omx32" data-mpi={i}>
                      
                                <span className="text-sm font-semibold text-rose-900" data-id="element-2300" data-mp="3cwlmo27av7ry">
                                  {p.name}
                                </span>
                              </div>)}
                          </div>
                        </div>}
                      {linkedInfo.teachers.length > 0 && <div data-id="element-2301" data-mp="s1zkc0qiq550">
                          <div className="flex items-center gap-2 mb-2" data-id="element-2302" data-mp="s298rjvx048l">
                            <GraduationCap className="w-4 h-4 text-orange-500" data-id="element-2303" data-mp="n593hb6w35ij" />
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-2304" data-mp="i41w3d8ns8ce">
                              Docenten
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-2305" data-mp="95827umppw2j">
                            {linkedInfo.teachers.map((t, i) => <div key={i} className="flex items-center gap-2 bg-orange-50 rounded-lg px-3 py-1.5 border border-orange-100" data-id="element-2306" data-mp="4cewp4jf6jpe" data-mpi={i}>
                      
                                <span className="text-sm font-semibold text-orange-900" data-id="element-2307" data-mp="3qkwr66j7sofx">
                                  {t.name}
                                </span>
                                {t.className && <span className="text-xs text-orange-600" data-id="element-2308" data-mp="3hjyihaxtzsr0">
                                    ({t.className})
                                  </span>}
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>}
            </div>
          </WorldZone>
        </motion.div>}

      <motion.div variants={itemVariants} data-id="element-2309" data-mp="2635hd69b0egn">
        <WorldZone title="Waar staan mijn gegevens?" icon={Shield} accentColor="bg-indigo-100 text-indigo-600" isLast={false} data-id="element-2310" data-mp="2b1gm52kqrys0">
          
          <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm space-y-4" data-id="element-2311" data-mp="1df9ibuw9iagp">
            {session.status === 'authenticated' ? <>
                <div className="flex items-start gap-4" data-id="element-2312" data-mp="1nce8s6x54s3b">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-sm border flex-shrink-0 ${shouldUseBackend() ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`} data-id="element-2313" data-mp="1najjkgeu6dtf">
                  
                    <Shield className="w-5 h-5" data-id="element-2314" data-mp="zlrzydfbb3s9" />
                  </div>
                  <div data-id="element-2315" data-mp="rlm17ujurif5">
                    <div className="font-bold text-slate-900" data-id="element-2316" data-mp="rl2hc8an6jyh">
                      {shouldUseBackend() ? 'Voortgang op de server' : 'Voortgang alleen op dit apparaat'}
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5" data-id="element-2317" data-mp="11bx6bkf9vxnm">
                      {shouldUseBackend() ? 'Je voortgang wordt bewaard en is op elk apparaat beschikbaar.' : 'Je inloggegevens staan wél op de server, maar je voortgang blijft op dit apparaat. Zonder toestemming van een ouder of school bewaren wij die niet.'}
                    </p>
                  </div>
                </div>

                {/* De muur, in gewone taal. Het onderscheid operationeel vs.
                   evaluatief is voor een gebruiker betekenisloos; wat telt is
                   wat er met zijn spullen gebeurt. */}
                {session.profile.role === 'learner' && <ul className="pt-4 border-t border-slate-100 space-y-2 text-sm" data-id="element-2318" data-mp="1u2fidaxz59d6">
                    <li className="flex items-start gap-2.5" data-id="element-2319" data-mp="1p0q83xikszq5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-2320" data-mp="36yldnt1b8qzo" />
                      <span className="text-slate-700" data-id="element-2321" data-mp="3by7s25svkda2">
                        Oefenen, je niveau en je streak — dit werkt altijd en
                        gaat mee naar een ander apparaat.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5" data-id="element-2322" data-mp="32xjch6nu24v2">
                      {session.profile.consentStatus === 'granted' || session.profile.dataController === 'school' ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-2323" data-mp="3uop757ksbacy" /> : <Shield className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-2324" data-mp="3gxalsu3emx2q" />}
                      <span className="text-slate-700" data-id="element-2325" data-mp="3lw5eqov3620k">
                        Toetsuitslagen bewaren
                        {session.profile.consentStatus === 'granted' || session.profile.dataController === 'school' ? ' — dit wordt bewaard.' : ' — hiervoor moet eerst een ouder of school aan je account gekoppeld zijn. Toetsen maken kan gewoon; de uitslag blijft alleen op dit apparaat.'}
                      </span>
                    </li>
                  </ul>}

                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 pt-4 border-t border-slate-100 text-sm" data-id="element-2326" data-mp="2kpz93ymloyxn">
                  <dt className="text-slate-500 font-medium" data-id="element-2327" data-mp="2gq757z85jgym">Ingelogd als</dt>
                  <dd className="text-slate-900 font-semibold" data-id="element-2328" data-mp="2bs5lrd6tdfmm">
                    {session.profile.displayName ?? '—'}
                  </dd>

                  <dt className="text-slate-500 font-medium" data-id="element-2329" data-mp="34fhyywr34b39">Rol</dt>
                  <dd className="text-slate-900 font-semibold" data-id="element-2330" data-mp="34gb8u6lcbgpm">
                    {getAccountRoleLabel(session.profile.role)}
                  </dd>

                  <dt className="text-slate-500 font-medium" data-id="element-2331" data-mp="2uq881k7z8ygq">
                    Wie is verantwoordelijk
                  </dt>
                  <dd className="text-slate-900 font-semibold" data-id="element-2332" data-mp="3s5vrsphhudyw">
                    {session.profile.dataController === 'school' ? 'De school' : 'Jij / je ouders'}
                  </dd>

                  <dt className="text-slate-500 font-medium" data-id="element-2333" data-mp="3uqk2woub6325">Toestemming</dt>
                  <dd className="text-slate-900 font-semibold" data-id="element-2334" data-mp="3ipdeethnkxy">
                    {{
                  granted: 'Gegeven',
                  pending: 'Aangevraagd',
                  withdrawn: 'Ingetrokken',
                  local: 'Niet nodig (lokaal)'
                }[session.profile.consentStatus]}
                  </dd>
                </dl>

                {/* Wie kijkt er mee — van de server, met de toestemmingsstatus
                   erbij. Zonder dit is "gekoppeld maar nog geen toestemming"
                   een onzichtbare tussentoestand, en dat is precies de fase
                   waarin een gebruiker zich afvraagt of het gelukt is. */}
                {session.profile.role === 'learner' && <div className="pt-4 border-t border-slate-100" data-id="element-2335" data-mp="109zzh0svz5ku">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2" data-id="element-2336" data-mp="107n85x18niv4">
                      Wie kan mijn voortgang zien?
                    </h4>
                    {serverParents === null ? <p className="text-sm text-slate-400" data-id="element-2337" data-mp="319cztc3loitl">Laden…</p> : serverParents.length === 0 ? <p className="text-sm text-slate-600" data-id="element-2338" data-mp="2xbysjpzhcl48">
                        Nog niemand. Geef je koppelcode aan je ouder of je
                        leerkracht.
                      </p> : <ul className="space-y-2" data-id="element-2339" data-mp="36a0rlzznl3ov">
                        {serverParents.map(p => <li key={p.id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg px-3 py-2" data-id="element-2340" data-mp="3kz6vw2dclk3m">
                    
                            <span className="text-sm font-semibold text-slate-800" data-id="element-2341" data-mp="3g0c2y7lo2f5s">
                              {p.name}
                            </span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${session.profile.consentStatus === 'granted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-2342" data-mp="3p11w13jjlrft">
                      
                              {session.profile.consentStatus === 'granted' ? 'Toestemming gegeven' : 'Gekoppeld — nog geen toestemming'}
                            </span>
                          </li>)}
                      </ul>}
                  </div>}

                <p className="text-xs text-slate-400 font-mono pt-2 border-t border-slate-100 break-all" data-id="element-2343" data-mp="1d6gu8didcl6">
                  {session.profile.id}
                </p>
              </> : <div className="flex items-start gap-4" data-id="element-2344" data-mp="5cf9tb0q56fo">
                <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shadow-sm border border-amber-100 flex-shrink-0" data-id="element-2345" data-mp="3nj38oj56kxcw">
                  <AlertCircle className="w-5 h-5" data-id="element-2346" data-mp="3ejpz7qh248s6" />
                </div>
                {/*
                 De oude tekst was "Account niet volledig aangemaakt — neem
                 contact op met de beheerder", ongeacht de oorzaak. Dat is in
                 het meest voorkomende geval (een haperende verbinding) ronduit
                 verkeerd advies: het presenteert een tijdelijke storing als een
                 kapot account, en stuurt iemand naar support voor iets wat één
                 knop oplost.
                  Nu volgt de tekst de oorzaak, en staat er bij een storing een
                 knop die daadwerkelijk helpt. "Neem contact op" blijft over voor
                 het enige geval waarin dat klopt: het profiel bestaat echt niet.
                 */}
                <div data-id="element-2347" data-mp="3lyyy8njz89u3">
                  <div className="font-bold text-slate-900" data-id="element-2348" data-mp="3hzr90pzsm5hf">
                    {session.status === 'loading' ? 'Bezig met controleren…' : session.status !== 'incomplete' ? 'Alleen op dit apparaat' : session.reason === 'error' ? 'Je gegevens zijn even niet op te halen' : 'Dit account mist een profiel'}
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5" data-id="element-2349" data-mp="3338x12jvbhh9">
                    {session.status !== 'incomplete' ? 'Er is geen serveraccount actief. Alles wat je doet blijft op dit apparaat.' : session.reason === 'error' ? 'Je bent ingelogd, maar de verbinding met de server lukte niet. Je kunt gewoon verder werken op dit apparaat — probeer het zo nog eens.' : 'Je bent ingelogd, maar er hoort geen profiel bij dit account. Dat is bij het aanmaken misgegaan en kun je zelf niet herstellen. Neem contact op met de beheerder.'}
                  </p>
                  {session.status === 'incomplete' && session.reason === 'error' && <Button onClick={handleRetryProfile} disabled={retryingProfile} variant="secondary" className="mt-3 text-sm" data-id="element-2350" data-mp="1ni806gqgtmz6">
                      <RefreshCw className={`w-4 h-4 mr-2 ${retryingProfile ? 'animate-spin' : ''}`} data-id="element-2351" data-mp="uuc6zm03ud75" />
                      {retryingProfile ? 'Bezig…' : 'Opnieuw proberen'}
                    </Button>}
                </div>
              </div>}
          </div>
        </WorldZone>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-2352" data-mp="14q5hz0y6dymt">
        <WorldZone title="Account" icon={LogOut} accentColor="bg-slate-100 text-slate-600" isLast={true} data-id="element-2353" data-mp="2b7itjd5hkf3a">
          
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4" data-id="element-2354" data-mp="2g63trckqmyrq">
            <div data-id="element-2355" data-mp="3s1p883p0w32v">
              <h3 className="text-lg font-black text-slate-800" data-id="element-2356" data-mp="3s28gesovfd3p">
                Sessie beëindigen
              </h3>
              <p className="text-slate-600 font-medium" data-id="element-2357" data-mp="tulg1fl7z2d">
                Je spullen blijven op dit apparaat staan, zodat je zo verder
                kunt.
              </p>
            </div>
            <Button onClick={onLogout} variant="secondary" className="w-full sm:w-auto text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900" data-id="element-2358" data-mp="ao37c6a44trk">
              
              <LogOut className="w-5 h-5 mr-2" data-id="element-2359" data-mp="3e38zy8bii4im" />
              Uitloggen
            </Button>
          </div>

          {/* Profielrooster. Standaard UIT: een rij namen op een klassikaal
               apparaat verraadt wie er in die klas zit, en dat is zelf een
               verwerking. De gebruiker zet het per apparaat aan. */}
          <div className="flex items-center justify-between gap-4 p-4 border-t border-slate-100" data-id="element-2360" data-mp="ydxov15dn6i5">
            <div data-id="element-2361" data-mp="12f0y34sx64ae">
              <h3 className="text-base font-black text-slate-800" data-id="element-2362" data-mp="2muz18ps7vu1o">
                Onthoud wie dit apparaat gebruikt
              </h3>
              <p className="text-sm text-slate-600" data-id="element-2363" data-mp="2iuwyseenqwlc">
                Dan staat je naam klaar op het inlogscherm. Handig als je met
                broer of zus dezelfde tablet deelt. Je wachtwoord blijft altijd
                nodig.
              </p>
            </div>
            <Toggle checked={rosterOn} onChange={v => {
            setRosterEnabled(v);
            setRosterOn(v);
          }} data-id="element-2364" data-mp="2458ztjty8a0s" />
            
          </div>

          {/* Tweede, zwaardere optie. Bewust apart en visueel rustiger dan
               "Uitloggen": dit is wat je kiest op een geleend of gedeeld
               apparaat, niet wat je dagelijks doet. */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-slate-100" data-id="element-2365" data-mp="1q7azvjgdkg4t">
            <div data-id="element-2366" data-mp="3kj8x0n1etlkc">
              <h3 className="text-base font-black text-slate-800" data-id="element-2367" data-mp="3kipqe2h7mjlm">
                Afmelden op dit apparaat
              </h3>
              <p className="text-sm text-slate-600" data-id="element-2368" data-mp="7gna92fv6nyp">
                Uitloggen én al je spullen van dit apparaat verwijderen. Handig
                op een geleende tablet of een computer op school.
              </p>
              {/*
                 De melding hoort hier en niet in de dialoog: die is dan al
                 gesloten. `assertive` omdat dit het antwoord is op een handeling
                 die het kind zojuist bewust deed — het mag niet wachten tot de
                 schermlezer toevallig langskomt.
                */}
              {wipeError && <p role="alert" aria-live="assertive" className="mt-2 text-sm font-semibold text-rose-600" data-id="element-2369" data-mp="h8j8cdhyae2x">
                
                  {wipeError}
                </p>}
            </div>
            <button type="button" onClick={() => {
            setWipeError(null);
            setWipeConfirmOpen(true);
          }} disabled={wiping} className="w-full sm:w-auto flex-shrink-0 px-4 py-2 rounded-xl text-sm font-bold text-rose-600 border border-rose-200 hover:bg-rose-50 disabled:opacity-50 transition-colors" data-id="element-2370" data-mp="31yidkf7m291u">
              
              Afmelden en wissen
            </button>
          </div>
        </WorldZone>
      </motion.div>

      <ConfirmDialog open={resetConfirmOpen} title="Voortgang wissen" message="Weet je zeker dat je alle voortgang wilt wissen? Dit kan niet ongedaan worden gemaakt." confirmLabel="Voortgang wissen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmResetProgress} onCancel={() => setResetConfirmOpen(false)} data-id="element-2371" data-mp="38dz0w0i0i80m" />
      

      {/* De waarschuwing moet KLOPPEN, niet alleen streng klinken. Wat er
           werkelijk verdwijnt hangt af van of dit account meesynchroniseert:
           oefenvoortgang staat dan ook op de server en komt terug bij de
           volgende keer inloggen. Toetsuitslagen staan altijd alleen hier —
           die zijn na dit moment echt weg. */}
      <ConfirmDialog open={wipeConfirmOpen} title="Afmelden op dit apparaat" message={!hasServerAccount ? 'Dit account staat alleen op dit apparaat. Als je afmeldt, is ALLES weg: je voortgang, je sterren en je toetsuitslagen. Je kunt het daarna niet meer terughalen.' : syncsToServer ? 'Je oefenvoortgang staat ook op de server en komt terug zodra je weer inlogt. Je toetsuitslagen staan alleen op dit apparaat en zijn daarna echt weg.' : 'Je oefenvoortgang staat ook op de server en komt terug zodra je weer inlogt. Je toetsuitslagen staan alleen op dit apparaat — dit account is niet gekoppeld aan een ouder — en zijn daarna echt weg.'} confirmLabel="Afmelden en wissen" cancelLabel="Annuleren" variant="danger" onConfirm={() => void confirmWipeDevice()} onCancel={() => setWipeConfirmOpen(false)} data-id="element-2372" data-mp="1rz7k6ku8ap75" />
      
    </PageContainer>;
}